import React, { useEffect } from 'react';

export default function OurMission() {
  // Add intersection observer for animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.animation = 'fadeInUp 1s ease-out forwards';
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div style={styles.container}>
      {/* Background decorative elements */}
      <div style={styles.backgroundDecor}>
        <div style={styles.decorLine1}></div>
        <div style={styles.decorLine2}></div>
        <div style={styles.decorLine3}></div>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title} className="fade-in">Our Mission</h2>
        
        <p style={styles.description} className="fade-in">
          At Global AI Society, we're not just building technology – we're building the future. Our team of 
          passionate innovators from 25+ countries works together to make AI education accessible to 
          everyone, everywhere. Join us in democratizing knowledge and empowering 150,000+ students 
          worldwide to become the AI leaders of tomorrow.
        </p>
      </div>

      {/* Subtle animated particles */}
      <div style={styles.particle1}></div>
      <div style={styles.particle2}></div>
      <div style={styles.particle3}></div>
      <div style={styles.particle4}></div>
      <div style={styles.particle5}></div>
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
    background: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #374151 100%)',
    padding: '100px 20px',
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },

  backgroundDecor: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.05,
  },

  decorLine1: {
    position: 'absolute',
    top: '20%',
    left: '0%',
    width: '300px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #8b5cf6, transparent)',
    animation: 'slideRight 8s linear infinite',
  },

  decorLine2: {
    position: 'absolute',
    bottom: '30%',
    right: '0%',
    width: '200px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #ec4899, transparent)',
    animation: 'slideLeft 10s linear infinite',
  },

  decorLine3: {
    position: 'absolute',
    top: '60%',
    left: '10%',
    width: '150px',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #3b82f6, transparent)',
    animation: 'slideRight 12s linear infinite',
  },

  content: {
    maxWidth: '1000px',
    textAlign: 'center',
    zIndex: 2,
    position: 'relative',
  },

  title: {
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    fontWeight: '700',
    color: 'white',
    marginBottom: '2.5rem',
    letterSpacing: '-0.02em',
    position: 'relative',
    display: 'inline-block',
  },

  description: {
    fontSize: 'clamp(1.1rem, 2.2vw, 1.4rem)',
    color: '#9ca3af',
    lineHeight: '1.8',
    maxWidth: '900px',
    margin: '0 auto',
    fontWeight: '400',
    letterSpacing: '0.01em',
  },

  particle1: {
    position: 'absolute',
    top: '15%',
    left: '10%',
    width: '3px',
    height: '3px',
    background: '#8b5cf6',
    borderRadius: '50%',
    animation: 'float 4s ease-in-out infinite',
    opacity: 0.6,
  },

  particle2: {
    position: 'absolute',
    top: '25%',
    right: '15%',
    width: '4px',
    height: '4px',
    background: '#ec4899',
    borderRadius: '50%',
    animation: 'float 6s ease-in-out infinite 1s',
    opacity: 0.5,
  },

  particle3: {
    position: 'absolute',
    bottom: '20%',
    left: '20%',
    width: '2px',
    height: '2px',
    background: '#3b82f6',
    borderRadius: '50%',
    animation: 'float 5s ease-in-out infinite 2s',
    opacity: 0.7,
  },

  particle4: {
    position: 'absolute',
    bottom: '35%',
    right: '25%',
    width: '5px',
    height: '5px',
    background: '#10b981',
    borderRadius: '50%',
    animation: 'float 7s ease-in-out infinite 0.5s',
    opacity: 0.4,
  },

  particle5: {
    position: 'absolute',
    top: '50%',
    left: '5%',
    width: '3px',
    height: '3px',
    background: '#f59e0b',
    borderRadius: '50%',
    animation: 'float 8s ease-in-out infinite 1.5s',
    opacity: 0.6,
  },
};

// Adding CSS animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes slideRight {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(calc(100vw + 300px)); }
  }

  @keyframes slideLeft {
    0% { transform: translateX(100%); }
    100% { transform: translateX(calc(-100vw - 200px)); }
  }

  @keyframes float {
    0%, 100% { 
      transform: translateY(0px) scale(1);
      opacity: 0.4;
    }
    50% { 
      transform: translateY(-15px) scale(1.2);
      opacity: 0.8;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .fade-in {
    opacity: 0;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .container {
      padding: 60px 15px;
    }
  }

  @media (max-width: 480px) {
    .container {
      padding: 40px 15px;
    }
  }
`;

document.head.appendChild(styleSheet);